import { useState, useCallback } from 'react';
import { useGraphData } from './data/useGraphData';
import { User } from '../types/api/graph.types';
import { Suggestion } from '../types/common/common.types';
import { createLogger } from '../../../../utils/logger';
import { SearchType } from '../../../../types/formData.types';

const logger = createLogger('useUnifiedSearch');

interface UseUnifiedSearchProps<T> {
  searchType: SearchType;
  setState?: (fn: (prev: T) => T) => void;
  setTagPickerSuggestions?: (suggestions: Suggestion[]) => void;
  minSearchLength?: number;
  office?: string;
}

interface UseUnifiedSearchReturn {
  suggestions: Suggestion[];
  isLoading: boolean;
  handleSelect: (selectedLabel: string) => void;
  handleSearch: (searchTerm: string) => Promise<void>;
  handleTagPickerSearch: (searchTerm: string) => Promise<Suggestion[]>;
  searchUsers: (searchTerm: string) => Promise<User[]>;
}

/**
 * Unified search hook that handles all types of user searches
 * Consolidates functionality from useSearch, usePartnerSearch, and useFeeEarnerSearch
 */
export const useUnifiedSearch = <
  T extends { selectedUser?: Suggestion; email?: string }
>({
  searchType,
  setState,
  setTagPickerSuggestions,
  minSearchLength = 3,
  office,
}: UseUnifiedSearchProps<T>): UseUnifiedSearchReturn => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { fetchPartners, fetchFeeEarners, fetchUsers } = useGraphData();

  // Get the appropriate fetch function based on search type
  const getFetchFunction = useCallback(() => {
    switch (searchType) {
      case 'partner':
        return fetchPartners;
      case 'feeEarner':
        return fetchFeeEarners;
      case 'default':
      default:
        return fetchUsers;
    }
  }, [searchType, fetchPartners, fetchFeeEarners, fetchUsers]);

  // Core search function
  const searchUsers = useCallback(
    async (searchTerm: string): Promise<User[]> => {
      if (!searchTerm || searchTerm.length < minSearchLength) {
        return [];
      }

      const fetchFunction = getFetchFunction();

      try {
        const users = await fetchFunction(searchTerm, office);
        return users || [];
      } catch (error: unknown) {
        logger.error(`Error searching ${searchType}:`, {
          error,
        });
        return [];
      }
    },
    [minSearchLength, getFetchFunction, office, searchType]
  );

  // Handle user selection from suggestions
  const handleSelect = useCallback(
    (selectedLabel: string) => {
      const selected = suggestions.find(
        (suggestion) => suggestion.label === selectedLabel
      );

      if (selected && setState) {
        setState((prev) => ({
          ...prev,
          selectedUser: selected,
          email: selected.email,
        }));
      }
    },
    [suggestions, setState]
  );

  // Handle main search functionality with state updates
  const handleSearch = useCallback(
    async (searchTerm: string) => {
      if (!setState) {
        logger.warning('setState not provided for handleSearch');
        return;
      }

      try {
        setIsLoading(true);
        setState((prev) => ({
          ...prev,
          isLoading: true,
          error: null,
        }));

        const users = await searchUsers(searchTerm);
        const newSuggestions: Suggestion[] = users.map((u) => ({
          label:
            `${u.givenName ?? ''} ${u.surname ?? ''}`.trim() || u.displayName,
          value: u.mail,
          email: u.mail,
        }));

        setSuggestions(newSuggestions);
      } catch (error: unknown) {
        setState((prev) => ({
          ...prev,
          error: `Error fetching ${searchType}`,
        }));
        logger.error(`Error in handleSearch for ${searchType}:`, { error });
      } finally {
        setIsLoading(false);
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    },
    [searchType, setState, searchUsers]
  );

  // Handle tag picker specific search
  const handleTagPickerSearch = useCallback(
    async (searchTerm: string): Promise<Suggestion[]> => {
      try {
        const users = await searchUsers(searchTerm);
        const newSuggestions: Suggestion[] = users.map((u) => ({
          label:
            `${u.givenName ?? ''} ${u.surname ?? ''}`.trim() || u.displayName,
          value: u.mail,
          email: u.mail,
        }));

        if (setTagPickerSuggestions) {
          setTagPickerSuggestions(newSuggestions);
        }

        return newSuggestions;
      } catch (error: unknown) {
        logger.error(`Error in tag picker search for ${searchType}:`, {
          error,
        });
        return [];
      }
    },
    [searchType, searchUsers, setTagPickerSuggestions]
  );

  return {
    suggestions,
    isLoading,
    handleSelect,
    handleSearch,
    handleTagPickerSearch,
    searchUsers,
  };
};

// Convenience hooks for specific search types
export const usePartnerSearch = <
  T extends { selectedUser?: Suggestion; email?: string }
>(
  props: Omit<UseUnifiedSearchProps<T>, 'searchType'>
) => useUnifiedSearch({ ...props, searchType: 'partner' });

export const useFeeEarnerSearch = <
  T extends { selectedUser?: Suggestion; email?: string }
>(
  props: Omit<UseUnifiedSearchProps<T>, 'searchType'>
) => useUnifiedSearch({ ...props, searchType: 'feeEarner' });

export const useDefaultSearch = <
  T extends { selectedUser?: Suggestion; email?: string }
>(
  props: Omit<UseUnifiedSearchProps<T>, 'searchType'>
) => useUnifiedSearch({ ...props, searchType: 'default' });
